import React from 'react';
import * as echarts from 'echarts/core';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import { LineChart } from 'echarts/charts';
import {
    TooltipComponent,
    ToolboxComponent,
    GridComponent,
    MarkAreaComponent,
    MarkLineComponent,
    DataZoomComponent,
    LegendScrollComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { EChartsOption, LineSeriesOption } from 'echarts';
import { TopLevelFormatterParams } from 'echarts/types/dist/shared';
import { toZonedTime } from 'date-fns-tz';
import { format } from 'date-fns';
import { renderTooltip } from './chartUtils';
import { CHART_DATA_COLOR } from '../../peak-load-forecast/Charts/chart';

echarts.use([
    LineChart,
    TooltipComponent,
    ToolboxComponent,
    GridComponent,
    CanvasRenderer,
    MarkAreaComponent,
    MarkLineComponent,
    DataZoomComponent,
    LegendScrollComponent,
]);

export interface IPerformanceChartData {
    timestamp: number;
    usageKw: number | null;
    baselineKw: number | null;
    nominationKw: number | null;
    isEventInterval: boolean;
}

interface IPerformanceChartProps {
    data: IPerformanceChartData[];
    timezone: string;
    /** when false, baseline and target (baseline - nomination) series are hidden */
    showBaseline?: boolean;
}

export const PerformanceChart = ({ data, timezone, showBaseline = true }: IPerformanceChartProps) => {
    const option: EChartsOption = {
        legend: {},
        grid: {
            top: '50px',
            left: '80px',
            right: '70px',
            bottom: '70px',
        },
        toolbox: {
            show: false,
            feature: {},
        },
        tooltip: {
            trigger: 'axis',
            axisPointer: {
                type: 'line',
                snap: true,
                lineStyle: {
                    type: 'solid',
                },
            },
            position: (point, params, dom, rect, size) => {
                const [tooltipWidth, tooltipHeight] = size.contentSize;

                return [point[0] - tooltipWidth / 2, point[1] - (tooltipHeight + 20)];
            },
            formatter: (params: TopLevelFormatterParams) => renderTooltip(params, timezone),
        },
        xAxis: {
            type: 'time',
            axisLabel: {
                color: '#697475',
                margin: 12,
                formatter: (value: number) => {
                    const zonedDate = toZonedTime(new Date(value), timezone);
                    return format(zonedDate, 'HH:mm');
                },
                hideOverlap: true,
            },
            axisLine: {
                show: true,
                lineStyle: {
                    color: '#d6dbdb',
                    width: 2,
                    type: 'solid',
                },
            },
            axisTick: {
                show: false,
            },
            splitLine: {
                show: true,
                lineStyle: {
                    color: '#d6dbdb',
                    width: 1,
                    type: 'solid',
                },
            },
            z: 1,
            min: data.map(item => item.timestamp).at(0),
            max: data.map(item => item.timestamp).at(-1),
            maxInterval: 60 * 60 * 1000,
        },
        yAxis: [
            {
                type: 'value',
                max: (value: any) => {
                    return Math.round(value.max * 1.1);
                },
                position: 'left',
                alignTicks: true,
                splitLine: {
                    show: false,
                },
                axisLabel: {
                    color: '#697475',
                    showMinLabel: false,
                    showMaxLabel: false,
                    formatter: (value: number) => value.toLocaleString() + ' kW',
                },
                axisLine: {
                    show: true,
                    lineStyle: {
                        color: '#d6dbdb',
                        width: 2,
                        type: 'solid',
                    },
                },
            },
        ],
        series: [
            {
                name: 'Usage',
                type: 'line',
                data: data.map(item => [item.timestamp, item.usageKw]),
                smooth: true,
                showSymbol: false,
                symbol: 'circle',
                symbolSize: 10,
                z: 2,
                color: CHART_DATA_COLOR,
                lineStyle: {
                    width: 2,
                    color: CHART_DATA_COLOR,
                },
                emphasis: {
                    lineStyle: {
                        color: CHART_DATA_COLOR,
                    },
                },
            },
            ...(showBaseline
                ? ([
                      {
                          name: 'Baseline',
                          type: 'line',
                          data: data.map(item => [item.timestamp, item.baselineKw]),
                          smooth: true,
                          showSymbol: false,
                          symbol: 'circle',
                          symbolSize: 10,
                          color: '#4A7088',
                          lineStyle: {
                              type: 'solid',
                              width: 2,
                              color: '#4A7088',
                          },
                          emphasis: {
                              lineStyle: {
                                  width: 2,
                                  color: '#4A7088',
                              },
                          },
                      },
                      {
                          name: 'Target (Baseline - Nomination)',
                          type: 'line',
                          data: data.map(item => [item.timestamp, item.nominationKw]),
                          smooth: true,
                          showSymbol: false,
                          symbol: 'circle',
                          symbolSize: 10,
                          color: '#CE7553',
                          lineStyle: {
                              type: 'solid',
                              width: 2,
                              color: '#CE7553',
                          },
                          z: 1,
                          areaStyle: {
                              color: '#F1D5C7',
                          },
                          emphasis: {
                              lineStyle: {
                                  type: 'solid',
                                  width: 2,
                                  color: '#CE7553',
                              },
                              areaStyle: {
                                  color: '#F1D5C7',
                              },
                          },
                      },
                  ] as LineSeriesOption[])
                : []),
            {
                // name: 'Event Duration',
                type: 'line',
                data: data.map(item => [item.timestamp, item.isEventInterval ? 50 : null]),
                showSymbol: false,
                symbolSize: 0,
                z: -10,
                color: 'transparent',
                markArea: {
                    itemStyle: {
                        color: 'rgba(187, 195, 195, 0.1)',
                    },
                    silent: true,
                    z: -10,
                    data: [
                        [
                            {
                                xAxis: data.at(0)?.timestamp,
                            },
                            {
                                xAxis: data.find(item => item.isEventInterval)?.timestamp,
                            },
                        ],
                        [
                            {
                                xAxis: data.filter(item => item.isEventInterval)?.at(-1)?.timestamp,
                            },
                            {
                                xAxis: data.at(-1)?.timestamp,
                            },
                        ],
                    ],
                },
                lineStyle: {
                    width: 0,
                },
            },
        ],
    };

    return <ReactEChartsCore echarts={echarts} option={option} style={{ height: '100%', width: '100%' }} notMerge lazyUpdate />;
};
